import type { ViewController } from '@presentation/shell/NexxusShell';
import { esc, fmtDate } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { SecureTenantStore } from '@infra/persistence/SecureTenantStore';
import { canMutateRecords } from '@application/access/ViewAccessPolicy';
import { uuid } from '@shared/utils/uuid';
import { getBusinessApiClient } from '@infra/api/BusinessApiClient';
import { isBrowserCloudHost } from '@application/licence/CloudWebHost';
import { peutExporterRgpd } from '@application/rgpd/ExportRgpd';
import { downloadBlob } from '@presentation/shell/download';
import { notifierToast } from '@presentation/views/comptabilite/panelKit';
import { mountExportRgpdPanel } from '@presentation/views/rgpd/ExportRgpdPanel';
import {
  bindCrudTable,
  crudActions,
  emptyRow,
  ensureIds,
  loadRegister,
  openRegisterForm,
  removeById,
  saveRegister,
  tenantIdOf,
  upsertById,
} from '@presentation/shell/localCrud';

interface DpoInfo {
  nom: string;
  fonction: string;
  email: string;
  tel: string;
  date: string;
  cnpdcp: string;
}

export interface TraitementLocal {
  id: string;
  nom: string;
  finalite: string;
  base_legale: string;
  donnees: string;
  conservation: string;
}

export interface DemandeRgpdLocal {
  id: string;
  date: string;
  demandeur: string;
  nature: string;
  delai: string;
  statut: string;
}

const DEFAULT_TRAITEMENTS: TraitementLocal[] = [
  {
    id: 'rgpd-paie',
    nom: 'Gestion de la paie',
    finalite: 'Établissement des bulletins et déclarations sociales',
    base_legale: 'Obligation légale (Loi 3/94)',
    donnees: 'État civil, RIB, salaire, cotisations',
    conservation: '10 ans',
  },
  {
    id: 'rgpd-rh',
    nom: 'Gestion des ressources humaines',
    finalite: 'Gestion des contrats, congés, carrières',
    base_legale: 'Exécution du contrat de travail',
    donnees: 'Identité, coordonnées, contrat, absences',
    conservation: '5 ans après départ',
  },
  {
    id: 'rgpd-tiers',
    nom: 'Comptabilité tiers',
    finalite: 'Suivi clients et fournisseurs',
    base_legale: 'Obligation légale (AUDCIF)',
    donnees: 'Raison sociale, NIF, coordonnées, factures',
    conservation: '10 ans',
  },
];

function legacyKey(ctx: Parameters<ViewController>[1], suffix: string): string {
  const tid = ctx.tenantContext.tryGetTenant()?.id ?? 'default';
  return `nexxus.rgpd.${tid}.${suffix}`;
}

function guard(can: boolean): boolean {
  if (can) return true;
  ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
  return false;
}

export const rgpdController: ViewController = async (section, ctx, shell) => {
  const tid = tenantIdOf(ctx);
  const canMutate = canMutateRecords(shell.permission);
  const dpoLegacy = legacyKey(ctx, 'dpo');
  const traitLegacy = legacyKey(ctx, 'traitements');

  const dpo = await SecureTenantStore.getJson<Partial<DpoInfo>>(tid, 'rgpd.dpo', {}, dpoLegacy);
  const set = (id: string, v: string | undefined): void => {
    const el = section.querySelector<HTMLInputElement | HTMLSelectElement>(id);
    if (el) el.value = v ?? '';
  };
  set('#dpo-nom', dpo.nom);
  set('#dpo-fonction', dpo.fonction);
  set('#dpo-email', dpo.email);
  set('#dpo-tel', dpo.tel);
  set('#dpo-date', dpo.date);
  set('#dpo-cnpdcp', dpo.cnpdcp ?? 'non');

  let traitements = ensureIds(
    await loadRegister<TraitementLocal>(tid, 'rgpd.traitements', DEFAULT_TRAITEMENTS, traitLegacy),
    'rgpd',
  );
  let demandes = await loadRegister<DemandeRgpdLocal>(tid, 'rgpd.demandes');

  const renderRegistre = (): void => {
    const tbody = section.querySelector('#rgpd-table tbody');
    if (!tbody) return;
    tbody.innerHTML =
      traitements.length === 0
        ? emptyRow(6, 'Aucun traitement déclaré.')
        : traitements
            .map(
              (t) => `<tr>
          <td><strong>${esc(t.nom)}</strong></td>
          <td>${esc(t.finalite)}</td>
          <td>${esc(t.base_legale)}</td>
          <td>${esc(t.donnees)}</td>
          <td>${esc(t.conservation)}</td>
          <td class="num">${crudActions(t.id)}</td>
        </tr>`,
            )
            .join('');
  };
  const renderDemandes = (): void => {
    const tbody = section.querySelector('#rgpd-demandes-table tbody');
    if (!tbody) return;
    tbody.innerHTML =
      demandes.length === 0
        ? emptyRow(6, 'Aucune demande enregistrée.')
        : demandes
            .map(
              (d) => `<tr>
          <td>${esc(fmtDate(d.date))}</td>
          <td>${esc(d.demandeur)}</td>
          <td>${esc(d.nature)}</td>
          <td>${esc(d.delai)}</td>
          <td><span class="pill">${esc(d.statut)}</span></td>
          <td class="num">${crudActions(d.id)}</td>
        </tr>`,
            )
            .join('');
  };
  renderRegistre();
  renderDemandes();

  const openTraitement = (existing?: TraitementLocal): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier le traitement' : 'Nouveau traitement',
      fields: [
        { kind: 'text', name: 'nom', label: 'Nom', required: true },
        { kind: 'text', name: 'finalite', label: 'Finalité', required: true },
        { kind: 'text', name: 'base_legale', label: 'Base légale', required: true },
        { kind: 'text', name: 'donnees', label: 'Données traitées', required: true },
        { kind: 'text', name: 'conservation', label: 'Durée de conservation', required: true },
      ],
      values: existing,
      onSubmit: async (data) => {
        const row: TraitementLocal = {
          id: existing?.id ?? uuid('rgpd'),
          nom: String(data['nom']),
          finalite: String(data['finalite']),
          base_legale: String(data['base_legale']),
          donnees: String(data['donnees']),
          conservation: String(data['conservation']),
        };
        traitements = upsertById(traitements, row);
        await saveRegister(tid, 'rgpd.traitements', traitements, traitLegacy);
        renderRegistre();
      },
    });
  };

  const openDemande = (existing?: DemandeRgpdLocal): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier la demande' : 'Nouvelle demande',
      fields: [
        { kind: 'date', name: 'date', label: 'Date', required: true },
        { kind: 'text', name: 'demandeur', label: 'Demandeur', required: true },
        {
          kind: 'select',
          name: 'nature',
          label: 'Nature',
          options: [
            { value: 'acces', label: 'Accès' },
            { value: 'rectification', label: 'Rectification' },
            { value: 'effacement', label: 'Effacement' },
            { value: 'opposition', label: 'Opposition' },
          ],
        },
        {
          kind: 'text',
          name: 'delai',
          label: 'Délai légal',
          required: true,
          placeholder: '30 jours',
        },
        {
          kind: 'select',
          name: 'statut',
          label: 'Statut',
          options: [
            { value: 'recue', label: 'Reçue' },
            { value: 'en_cours', label: 'En cours' },
            { value: 'close', label: 'Close' },
          ],
        },
      ],
      values: existing,
      onSubmit: async (data) => {
        const row: DemandeRgpdLocal = {
          id: existing?.id ?? uuid('dmd'),
          date: String(data['date']),
          demandeur: String(data['demandeur']),
          nature: String(data['nature']),
          delai: String(data['delai']),
          statut: String(data['statut']),
        };
        demandes = upsertById(demandes, row);
        await saveRegister(tid, 'rgpd.demandes', demandes);
        renderDemandes();
      },
    });
  };

  bindCrudTable(
    section.querySelector('#rgpd-table tbody'),
    section,
    (id) => openTraitement(traitements.find((x) => x.id === id)),
    async (id) => {
      traitements = removeById(traitements, id);
      await saveRegister(tid, 'rgpd.traitements', traitements, traitLegacy);
      renderRegistre();
    },
    canMutate,
  );
  bindCrudTable(
    section.querySelector('#rgpd-demandes-table tbody'),
    section,
    (id) => openDemande(demandes.find((x) => x.id === id)),
    async (id) => {
      demandes = removeById(demandes, id);
      await saveRegister(tid, 'rgpd.demandes', demandes);
      renderDemandes();
    },
    canMutate,
  );

  // Export de portabilité (Cloud) : administrateur seulement, API du serveur (GET /v1/export/...).
  const ongletExport = section.querySelector<HTMLElement>('#tab-btn-rgpd-export');
  const hoteExport = section.querySelector<HTMLElement>('#rgpd-export-host');
  if (ongletExport && hoteExport) {
    const autorise = peutExporterRgpd(isBrowserCloudHost(), shell.permission);
    ongletExport.hidden = !autorise;
    if (autorise) {
      const salaries = await ctx.salaries.list();
      mountExportRgpdPanel(hoteExport, {
        personnes: salaries.map((s) => ({ id: s.id, libelle: `${s.prenom} ${s.nom} (${s.matricule})` })),
        exporterTout: (format) => getBusinessApiClient().exportRgpdComplet(format),
        exporterPersonne: (id, format) => getBusinessApiClient().exportRgpdPersonne(id, format),
        telecharger: downloadBlob,
        notify: notifierToast,
      });
    }
  }

  const w = window as unknown as Record<string, unknown>;
  w['enregistrerDPO'] = async () => {
    if (!guard(canMutate)) return;
    const get = (id: string): string =>
      section.querySelector<HTMLInputElement | HTMLSelectElement>(id)?.value.trim() ?? '';
    const data: DpoInfo = {
      nom: get('#dpo-nom'),
      fonction: get('#dpo-fonction'),
      email: get('#dpo-email'),
      tel: get('#dpo-tel'),
      date: get('#dpo-date'),
      cnpdcp: get('#dpo-cnpdcp'),
    };
    if (!data.nom) {
      ToastService.warning('Le nom du DPO est requis');
      return;
    }
    await SecureTenantStore.setJson(tid, 'rgpd.dpo', data, dpoLegacy);
    ToastService.success('Désignation du DPO enregistrée');
  };
  w['openTraitementModal'] = () => openTraitement();
  w['openDemandeRGPDModal'] = () => openDemande();
};
